import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { helperAPI } from '../services/api';
import StarRating from '../components/StarRating';
import MapView from '../components/MapView';
import FilterModal from '../components/FilterModal';
import '../styles/HelpersList.css';

const HelpersList = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [helpers, setHelpers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    service: searchParams.get('service') || '',
    location: searchParams.get('location') || '',
    gender: searchParams.get('gender') || '',
    minAge: parseInt(searchParams.get('minAge') || '18'),
    maxAge: parseInt(searchParams.get('maxAge') || '100'),
    minPrice: parseInt(searchParams.get('minPrice') || '0'),
    maxPrice: parseInt(searchParams.get('maxPrice') || '2000'),
    services: []
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    pages: 0
  });
  const [showMap, setShowMap] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [showFilterModal, setShowFilterModal] = useState(false);

  const serviceOptions = [
    { value: '', label: 'All Services' },
    { value: 'sweeping-mopping', label: '🧹 Sweeping & Mopping' },
    { value: 'cooking', label: '👨‍🍳 Cooking' },
    { value: 'laundry', label: '👕 Laundry' },
    { value: 'gardening', label: '🌱 Gardening' },
    { value: 'pet-care', label: '🐕 Pet Care' },
    { value: 'elderly-care', label: '👴 Elderly Care' },
    { value: 'babysitting', label: '👶 Babysitting' },
    { value: 'hair-dresser', label: '✂️ Hair Dresser' },
    { value: 'other', label: '🔧 Other' }
  ];

  useEffect(() => {
    loadHelpers();
  }, [filters, pagination.page]);

  const loadHelpers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const queryParams = {
        page: pagination.page,
        limit: pagination.limit
      };
      
      // Add basic filters
      if (filters.service) queryParams.service = filters.service;
      if (filters.location) queryParams.location = filters.location;
      
      // Add advanced filters
      if (filters.gender) queryParams.gender = filters.gender;
      if (filters.minAge) queryParams.minAge = filters.minAge;
      if (filters.maxAge) queryParams.maxAge = filters.maxAge;
      if (filters.minPrice) queryParams.minPrice = filters.minPrice;
      if (filters.maxPrice) queryParams.maxPrice = filters.maxPrice;
      if (filters.services && filters.services.length > 0) {
        queryParams.services = filters.services.join(',');
      }
      
      // Add geolocation parameters if user location is available
      if (userLocation) {
        queryParams.lat = userLocation.lat;
        queryParams.lng = userLocation.lng;
        queryParams.radius = 50; // 50km radius
      }

      const response = await helperAPI.getHelpers(queryParams);
      setHelpers(response.data.helpers);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages
      }));
      
    } catch (err) {
      setError('Failed to load helpers. Please try again.');
      console.error('Load helpers error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
    
    // Update URL params
    const params = new URLSearchParams();
    if (newFilters.service) params.set('service', newFilters.service);
    if (newFilters.location) params.set('location', newFilters.location);
    if (newFilters.gender) params.set('gender', newFilters.gender);
    if (newFilters.minAge !== 18) params.set('minAge', newFilters.minAge);
    if (newFilters.maxAge !== 100) params.set('maxAge', newFilters.maxAge);
    if (newFilters.minPrice !== 0) params.set('minPrice', newFilters.minPrice);
    if (newFilters.maxPrice !== 2000) params.set('maxPrice', newFilters.maxPrice);
    
    const newURL = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, '', newURL);
  };

  const handleBookNow = (helper) => {
    navigate(`/book?helperId=${helper._id}`);
  };

  const handleViewProfile = (helper) => {
    navigate(`/helper/${helper._id}`);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getServiceLabel = (service) => {
    const option = serviceOptions.find(opt => opt.value === service);
    return option ? option.label : service;
  };

  const handleMapLocationSelect = async (latlng) => {
    try {
      // Update user location for geospatial search
      setUserLocation(latlng);
      
      // Reverse geocoding to get location name from coordinates
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latlng.lat}&lon=${latlng.lng}&addressdetails=1`
      );
      const data = await response.json();
      
      if (data && data.display_name) {
        // Extract city/area from the response
        const city = data.address?.city || data.address?.town || data.address?.village || 
                    data.address?.state_district || data.address?.state || '';
        handleFilterChange({...filters, location: city});
      }
    } catch (error) {
      console.error('Reverse geocoding error:', error);
    }
  };

  const handleMapHelperSelect = (helper) => {
    handleViewProfile(helper);
  };
 
  const toggleMapView = () => {
    setShowMap(!showMap);
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const location = { lat: latitude, lng: longitude };
          setUserLocation(location);
          
          // Reverse geocode to get location name
          handleMapLocationSelect(location);
        },
        (error) => {
          console.error('Geolocation error:', error);
          alert('Unable to get your location. Please enable location services.');
        }
      );
    } else {
      alert('Geolocation is not supported by this browser.');
    }
  };

  const openFilterModal = () => {
    setShowFilterModal(true);
  };

  const closeFilterModal = () => {
    setShowFilterModal(false);
  };

  const handleApplyFilters = (newFilters) => {
    handleFilterChange(newFilters);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.gender) count++;
    if (filters.minAge !== 18 || filters.maxAge !== 100) count++;
    if (filters.minPrice !== 0 || filters.maxPrice !== 2000) count++;
    if (filters.services && filters.services.length > 0) count++;
    return count;
  };

  if (loading && helpers.length === 0) {
    return (
      <div className="helpers-list-page">
        <div className="container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Finding helpers for you...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="helpers-list-page">
      <div className="container">
        <div className="page-header">
          <h1>🔍 Find Helpers</h1>
          <p>Browse qualified helpers in your area</p>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Filters Section */}
        <div className="filters-section">
          <div className="filters">
            <div className="filter-group">
              <label htmlFor="service-filter">Service Type:</label>
              <select
                id="service-filter"
                value={filters.service}
                onChange={(e) => handleFilterChange({...filters, service: e.target.value})}
                className="filter-select"
              >
                {serviceOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="location-filter">Location:</label>
              <div className="location-input-group">
                <input
                  id="location-filter"
                  type="text"
                  placeholder="Enter city or area"
                  value={filters.location}
                  onChange={(e) => handleFilterChange({...filters, location: e.target.value})}
                  className="filter-input"
                />
                <button 
                  className="location-btn-small"
                  onClick={getCurrentLocation}
                  title="Use my current location"
                  style={{ 
                    background: '#4F46E5', 
                    color: 'white', 
                    border: 'none',
                    boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)'
                  }}
                >
                  📍
                </button>
              </div>
            </div>

            <button 
              className="advanced-filters-btn"
              onClick={openFilterModal}
              style={{ 
                background: '#4F46E5', 
                color: 'white', 
                border: 'none',
                boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)'
              }}
            >
              <i className="fas fa-sliders-h"></i> Filters
              {getActiveFiltersCount() > 0 && (
                <span className="filter-badge">{getActiveFiltersCount()}</span>
              )}
            </button>

            <button 
              className="clear-filters-btn"
              onClick={() => handleFilterChange({ 
                service: '', 
                location: '',
                gender: '',
                minAge: 18,
                maxAge: 100,
                minPrice: 0,
                maxPrice: 2000,
                services: []
              })}
              disabled={!filters.service && !filters.location && getActiveFiltersCount() === 0}
              style={{ 
                background: (!filters.service && !filters.location && getActiveFiltersCount() === 0) ? '#9CA3AF' : '#4F46E5', 
                color: 'white', 
                border: 'none',
                boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                opacity: (!filters.service && !filters.location && getActiveFiltersCount() === 0) ? '0.7' : '1'
              }}
            >
              <i className="fas fa-times"></i> Clear All
            </button>

            <button 
              className="map-toggle-btn"
              onClick={toggleMapView}
              style={{ 
                background: '#4F46E5', 
                color: 'white', 
                border: 'none',
                boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)'
              }}
            >
              {showMap ? <><i className="fas fa-list"></i> List View</> : <><i className="fas fa-map-marked-alt"></i> Map View</>}
            </button>
          </div>

          <div className="results-summary">
            {loading ? (
              <span>Searching...</span>
            ) : (
              <span>
                {pagination.total} helper{pagination.total !== 1 ? 's' : ''} found
                {filters.service && ` for ${getServiceLabel(filters.service)}`}
                {filters.location && ` in ${filters.location}`}
                {getActiveFiltersCount() > 0 && ' (filtered)'}
              </span>
            )}
          </div>
        </div>

        {/* Filter Modal */}
        <FilterModal 
          isOpen={showFilterModal}
          onClose={closeFilterModal}
          initialFilters={filters}
          onApplyFilters={handleApplyFilters}
        />

        {/* Map View */}
        {showMap && (
          <MapView
            helpers={helpers}
            onHelperSelect={handleMapHelperSelect}
            onLocationSelect={handleMapLocationSelect}
            searchLocation={filters.location}
            userLocation={userLocation}
            height="500px"
          />
        )}

        {/* Helpers Grid */}
        {helpers.length === 0 && !loading ? (
          <div className="no-helpers">
            <div className="no-helpers-icon">🔍</div>
            <h3>No helpers found</h3>
            <p>
              {(filters.service || filters.location || getActiveFiltersCount() > 0)
                ? 'Try adjusting your filters or search in a different area.'
                : 'No helpers are currently available. Please check back later.'
              }
            </p>
            <button 
              className="browse-btn"
              onClick={() => handleFilterChange({ 
                service: '', 
                location: '',
                gender: '',
                minAge: 18,
                maxAge: 100,
                minPrice: 0,
                maxPrice: 2000,
                services: []
              })}
              style={{ 
                background: '#4F46E5', 
                color: 'white', 
                border: 'none',
                boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              Show All Helpers
            </button>
          </div>
        ) : (
          <>
            <div className="helpers-grid">
              {helpers.map(helper => (
                <div key={helper._id} className="helper-card">
                  <div className="helper-avatar">
                    {helper.name.charAt(0).toUpperCase()}
                  </div>
                  
                  <div className="helper-info">
                    <h3>{helper.name}</h3>
                    <p className="helper-location">📍 {helper.location}</p>
                    
                    <div className="helper-services">
                      {helper.services.slice(0, 4).map(service => (
                        <span key={service} className="service-tag">
                          {getServiceLabel(service)}
                        </span>
                      ))}
                      {helper.services.length > 4 && (
                        <span className="service-tag more-services">
                          +{helper.services.length - 4} more
                        </span>
                      )}
                    </div>
                    
                    <div className="helper-stats">
                      <div className="helper-rating">
                        <StarRating 
                          rating={helper.rating || 0} 
                          readonly={true} 
                          size="small"
                          showText={false}
                        />
                        <span className="review-count">({helper.totalReviews || 0} reviews)</span>
                      </div>
                      <div className="helper-rate">
                        💰 ₹{helper.hourlyRate}/hour
                      </div>
                    </div>

                    {helper.bio && (
                      <p className="helper-bio">
                        {helper.bio.length > 100 
                          ? `${helper.bio.substring(0, 100)}...` 
                          : helper.bio
                        }
                      </p>
                    )}

                    <div className="helper-demographics">
                      {helper.gender && (
                        <span className="helper-gender">
                          {helper.gender === 'male' ? '👨' : helper.gender === 'female' ? '👩' : '👤'} {helper.gender.charAt(0).toUpperCase() + helper.gender.slice(1)}
                        </span>
                      )}
                      {helper.age && (
                        <span className="helper-age">
                          🎂 {helper.age} years
                        </span>
                      )}
                    </div>

                    <div className="availability-indicator">
                      {helper.isActive ? (
                        <span className="status-available">✅ Available</span>
                      ) : (
                        <span className="status-unavailable">❌ Unavailable</span>
                      )}
                    </div>
                  </div>

                  <div className="helper-actions">
                    <button 
                      className="btn-primary"
                      onClick={() => handleBookNow(helper)}
                      disabled={!helper.isActive}
                    >
                      📅 Book Now
                    </button>
                    <button 
                      className="btn-secondary"
                      onClick={() => handleViewProfile(helper)}
                    >
                      👤 View Profile
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  ← Previous
                </button>
                
                <div className="page-numbers">
                  {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                    let pageNum;
                    if (pagination.pages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.pages - 2) {
                      pageNum = pagination.pages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        className={`page-number ${pageNum === pagination.page ? 'active' : ''}`}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}

        {loading && helpers.length > 0 && (
          <div className="loading-overlay">
            <div className="spinner"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelpersList; 